import { borderGradient } from "@/containers/GradientContainer/gradientContainerUtils";
import { MetaRatingIcon } from "@/designSystemIcons";
import { metaRatingUpgradeFormatter } from "@/functions/metaRatingUpgradeFormatter";
import { cn } from "@/lib/utils";
import { Tier as TierType } from "@/redux/players/playersSlice";
import { useMemo } from "react";

import { MetaRatingExplainer } from "./MetaRatingExplainer";
import { Tier } from "./Tier/Tier";

// neutral-600 → transparent ring over a neutral-750 fill; hover lifts both a
// shade via the --border-gradient-* vars (transition-all animates the fill).
export const chipGradient = borderGradient({
  from: "neutral-600",
  to: "transparent",
  fill: "neutral-750",
});

interface MetaRatingChipProps {
  metaRating: number;
  /** Omitted for players with a meta rating below the lowest tier. */
  tier?: TierType;
  basePlayerMetaRating?: number;
  metaRatingDelta?: number;
  basePlayerTier?: TierType;
}

export const MetaRatingChip = ({
  metaRating,
  tier,
  basePlayerMetaRating,
  metaRatingDelta,
  basePlayerTier,
}: MetaRatingChipProps) => {
  const diff = useMemo(() => {
    if (metaRatingDelta != null) return metaRatingDelta;
    return basePlayerMetaRating
      ? metaRating - (basePlayerMetaRating ?? 0)
      : undefined;
  }, [metaRating, basePlayerMetaRating, metaRatingDelta]);

  return (
    <MetaRatingExplainer>
      <div
        className={cn(
          "flex flex-row rounded-[20px] px-2 py-1 gap-x-2 text-sm transition-all duration-200",
          chipGradient.className,
          "group-hover:[--border-gradient-from:var(--color-neutral-500)]",
          "group-hover:[--border-gradient-fill:var(--color-neutral-700)]",
        )}
        style={chipGradient.style}
      >
        <div className="flex flex-row gap-x-1 items-center font-semibold">
          <MetaRatingIcon width="16" height="16" />
          <div className="flex flex-row items-center">
            <p>{metaRating?.toFixed(1) || "-"}</p>
            <MetaRatingChange value={diff} />
          </div>
        </div>
        {tier && <Tier tier={tier} basePlayerTier={basePlayerTier} />}
      </div>
    </MetaRatingExplainer>
  );
};

/**
 * A change in meta rating: green behind an up arrow for a rise, red behind a
 * down arrow for a drop. Nothing for a change that rounds to 0.0.
 */
export const MetaRatingChange = ({
  value,
  className,
}: {
  value?: number;
  className?: string;
}) => {
  const formatted = metaRatingUpgradeFormatter(
    value != null ? Math.abs(value) : undefined,
  );
  if (!formatted || value == null) return null;
  const down = value < 0;
  return (
    <span
      className={cn(
        "flex flex-row items-center tabular-nums",
        down ? "text-error" : "text-upgrade",
        className,
      )}
    >
      <span className={cn("flex", down && "rotate-180")}>
        <MetaRatingUpgradeArrow />
      </span>
      <span>{formatted}</span>
    </span>
  );
};

export const MetaRatingUpgradeArrow = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5.5 4.25L8 1.75L10.5 4.25"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 1.75V14.25"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
